import {
  Combobox,
  Group,
  Text,
  useCombobox,
  Stack,
  StackProps,
  ComboboxProps,
  rem,
} from '@mantine/core';
import { PiCaretDownBold } from 'react-icons/pi';
import { Token } from 'src/components/Token/Token';

export type SelectOption = {
  value: string;
  label?: string;
};

export function Select({
  options,
  label,
  value: selectedValue,
  isTokenSelect = false,
  withinPortal = true,
  stackProps,
  comboboxProps,
  onChange,
  optionRenderer,
}: {
  options: SelectOption[];
  label?: React.ReactNode;
  value?: string;
  isTokenSelect?: boolean;
  withinPortal?: boolean;
  stackProps?: StackProps;
  comboboxProps?: ComboboxProps;
  onChange?: (newValue: string) => void;
  optionRenderer?: (
    option: SelectOption,
    labelNode: React.ReactNode
  ) => React.ReactNode;
}) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const isClickable = options.length > 1;

  const renderOption = (value?: string) => {
    if (typeof value === 'undefined') {
      return null;
    }

    const option = options.find((i) => i.value === value);

    const labelNode = (
      <Text fz={16} fw={500} tt="capitalize">
        {option?.label || value}
      </Text>
    );

    if (optionRenderer) {
      return optionRenderer(option!, labelNode);
    }

    return (
      <Group gap={8} wrap="nowrap" style={{ flexShrink: 0 }}>
        {isTokenSelect && <Token.Icon symbol={value} size={22} />}
        {labelNode}
      </Group>
    );
  };

  return (
    <Stack gap={12} align="flex-start" {...stackProps}>
      {label && (
        <Text fz={16} fw={500}>
          {label}
        </Text>
      )}

      <Combobox
        store={combobox}
        withinPortal={withinPortal}
        width="max-content"
        position="bottom-start"
        {...comboboxProps}
        onOptionSubmit={(val) => {
          onChange?.(val);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <Group
            gap={8}
            h={40}
            px={12}
            bg="#171B21"
            wrap="nowrap"
            justify="space-between"
            style={{
              flexShrink: 0,
              borderRadius: rem(8),
              border: '1px solid #ffffff14',
              cursor: isClickable ? 'pointer' : 'default',
            }}
            onClick={() => isClickable && combobox.toggleDropdown()}
          >
            {renderOption(selectedValue)}
            {isClickable && (
              <PiCaretDownBold size={16} style={{ flexShrink: 0 }} />
            )}
          </Group>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {options.map((o) => {
              return (
                <Combobox.Option value={o.value} key={o.value}>
                  {renderOption(o.value)}
                </Combobox.Option>
              );
            })}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Stack>
  );
}
